import { useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Empty, EmptyDescription, EmptyMedia } from '@/components/ui/empty'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useBreadcrumbFromRoute } from '@/hooks/use-breadcrumb-from-route'
import { useCompany } from '@/stores/companies.store'
import { useNotes } from '@/stores/notes.store'
import { useAttachments } from '@/stores/attachments.store'
import { useTasksList } from '@/stores/tasks.store'
import { CompanyCreateEditSheet } from '@/components/crm/company-create-edit-sheet'
import { CompanyArchiveMenu } from '@/components/crm/company-archive-menu'
import { AddContactToCompanyDialog } from '@/components/crm/add-contact-to-company-dialog'
import { RemoveContactFromCompanyDialog } from '@/components/crm/remove-contact-from-company-dialog'
import { ActivityTabs } from '@/components/shared/activity-tabs'
import { ActionGate } from '@/components/shared/action-gate'
import { AccessDeniedState } from '@/components/shared/access-denied-state'
import { isPermissionDeniedError } from '@/lib/error-reporting'
import { ParentTypeEnum } from '@/gen/cortado/v1/settings_pb'
import {
  AtSign,
  Building2,
  Globe,
  Calendar,
  Users,
  ExternalLink,
  Mail,
  Phone,
  Pencil,
  Plus,
  X
} from 'lucide-react'
import { formatTimestamp } from '@/lib/date-utils'
import { formatFullName, formatPhoneNumber } from '@/lib/utils'

export const Route = createFileRoute('/_app/_crm/companies/$id')({
  component: CompanyDetailPage,
})

function CompanyDetailPage() {
  const { id } = Route.useParams()
  useBreadcrumbFromRoute()

  const { company, contacts, isLoading, isError, refetch, error } = useCompany(id)

  // Notes and tasks for this company
  const { notes } = useNotes({
    parentType: ParentTypeEnum.PARENT_TYPE_COMPANY,
    parentId: id,
  })
  const { tasks } = useTasksList({
    parentType: ParentTypeEnum.PARENT_TYPE_COMPANY,
    parentId: id,
  })
  const { attachments } = useAttachments({
    parentType: ParentTypeEnum.PARENT_TYPE_COMPANY,
    parentId: id,
  })

  // Dialog states
  const [editSheetOpen, setEditSheetOpen] = useState(false)
  const [addContactDialogOpen, setAddContactDialogOpen] = useState(false)
  const [removeContactDialogOpen, setRemoveContactDialogOpen] = useState(false)
  const [contactToRemove, setContactToRemove] = useState<{ id: string; name: string } | null>(null)

  const handleRemoveContactClick = (contactId: string, contactName: string) => {
    setContactToRemove({ id: contactId, name: contactName })
    setRemoveContactDialogOpen(true)
  }

  const getContactDisplayName = (contact: { firstName?: string; lastName?: string; email?: string; phoneNumber?: string }) => (
    formatFullName(contact.firstName, contact.lastName, contact.email || contact.phoneNumber || 'Unknown contact')
  )

  if (isLoading) {
    return (
      <div className="flex h-full flex-col gap-4 p-4">
        {/* Header Skeleton */}
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <Skeleton className="h-16 w-16 rounded-lg" />
            <div className="space-y-2">
              <Skeleton className="h-8 w-64" />
              <Skeleton className="h-4 w-48" />
            </div>
          </div>
        </div>

        {/* Content Grid Skeleton */}
        <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <Skeleton className="h-6 w-40" />
              </CardHeader>
              <CardContent className="space-y-4">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <Skeleton className="h-4 w-4" />
                    <Skeleton className="h-4 w-32" />
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          <div className="space-y-4">
            <Card>
              <CardHeader>
                <Skeleton className="h-6 w-24" />
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    )
  }

  if (isError || !company) {
    if (isPermissionDeniedError(error)) {
      return <AccessDeniedState variant="page" noun="companies" />
    }
    return (
      <div className="flex h-full flex-col gap-4 p-4">
        <Empty className="flex-1 border border-dashed py-12">
          <EmptyMedia variant="icon">
            <Building2 className="h-6 w-6" />
          </EmptyMedia>
          <EmptyDescription>We couldn't load this company right now.</EmptyDescription>
        </Empty>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="flex-shrink-0 h-16 w-16 rounded-lg bg-muted flex items-center justify-center">
            <Building2 className="h-8 w-8 text-muted-foreground" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-semibold">{company.name}</h1>
              {company.archivedAt && <Badge variant="secondary">Archived</Badge>}
            </div>
            {company.websiteUrl && (
              <a
                href={company.websiteUrl.startsWith('http') ? company.websiteUrl : `https://${company.websiteUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
              >
                <Globe className="h-3 w-3" />
                {company.websiteUrl.replace(/^https?:\/\//, '')}
                <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ActionGate
            organizationId={company.organizationId}
            permission="ContactsManage"
            verb="edit"
            noun="companies"
          >
            <Button variant="outline" onClick={() => setEditSheetOpen(true)}>
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </Button>
          </ActionGate>
          <CompanyArchiveMenu company={company} />
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
        {/* Left Column - Company Info */}
        <div className="space-y-4">
          {/* Company Information Card */}
          <Card>
            <CardHeader>
              <CardTitle>Company Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-3">
                <Building2 className="h-4 w-4 mt-0.5 text-muted-foreground" />
                <div className="space-y-1 flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground">Name</p>
                  <p className="text-sm">{company.name}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Globe className="h-4 w-4 mt-0.5 text-muted-foreground" />
                <div className="space-y-1 flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground">Website</p>
                  {company.websiteUrl ? (
                    <a
                      href={company.websiteUrl.startsWith('http') ? company.websiteUrl : `https://${company.websiteUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm truncate flex items-center gap-1 hover:text-foreground hover:underline transition-colors"
                    >
                      <span className="truncate">{company.websiteUrl.replace(/^https?:\/\//, '')}</span>
                      <ExternalLink className="h-3 w-3 flex-shrink-0" />
                    </a>
                  ) : (
                    <p className="text-sm">Not provided</p>
                  )}
                </div>
              </div>

              <div className="flex items-start gap-3">
                <AtSign className="h-4 w-4 mt-0.5 text-muted-foreground" />
                <div className="space-y-1 flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground">Email domain</p>
                  <p className="text-sm truncate">{company.domain || 'Not provided'}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Calendar className="h-4 w-4 mt-0.5 text-muted-foreground" />
                <div className="space-y-1 flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground">Created</p>
                  <p className="text-sm">
                    {company.createdAt ? formatTimestamp(company.createdAt, 'MMM d, yyyy') : '-'}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Calendar className="h-4 w-4 mt-0.5 text-muted-foreground" />
                <div className="space-y-1 flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground">Last Updated</p>
                  <p className="text-sm">
                    {company.updatedAt ? formatTimestamp(company.updatedAt, 'MMM d, yyyy') : '-'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Contacts */}
        <div className="space-y-4">
          {/* Contacts Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Contacts
                    {contacts.length > 0 && (
                      <span className="text-xs bg-muted px-2 py-0.5 rounded-full font-normal">
                        {contacts.length}
                      </span>
                    )}
                  </CardTitle>
                  <CardDescription>
                    People associated with this company
                  </CardDescription>
                </div>
                <ActionGate
                  organizationId={company.organizationId}
                  permission="ContactsManage"
                  verb="add"
                  noun="company contacts"
                >
                  <Button size="sm" onClick={() => setAddContactDialogOpen(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Contact
                  </Button>
                </ActionGate>
              </div>
            </CardHeader>
            <CardContent>
              {contacts.length > 0 ? (
                <div className="space-y-2">
                  {contacts.map((companyContact) => {
                    const contact = companyContact.contact
                    if (!contact) return null

                    return (
                      <div
                        key={contact.id}
                        className="flex items-center gap-2 p-3 rounded-md border hover:bg-muted/50 transition-colors group"
                      >
                        <Link
                          to="/contacts/$id"
                          params={{ id: contact.id }}
                          className="flex-1 min-w-0"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium">
                                  {getContactDisplayName(contact)}
                                </span>
                                {contact.archivedAt && <Badge variant="secondary">Archived</Badge>}
                                {companyContact.title && (
                                  <span className="text-xs bg-muted px-2 py-0.5 rounded">
                                    {companyContact.title}
                                  </span>
                                )}
                              </div>
                              <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                                {contact.email && (
                                  <span className="flex items-center gap-1">
                                    <Mail className="h-3 w-3" />
                                    {contact.email}
                                  </span>
                                )}
                                {contact.phoneNumber && (
                                  <span className="flex items-center gap-1">
                                    <Phone className="h-3 w-3" />
                                    {formatPhoneNumber(contact.phoneNumber)}
                                  </span>
                                )}
                              </div>
                            </div>
                            <ExternalLink className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity mt-1" />
                          </div>
                        </Link>
                        <ActionGate
                          organizationId={company.organizationId}
                          permission="ContactsManage"
                          verb="remove"
                          noun="company contacts"
                        >
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                            onClick={(e) => {
                              e.preventDefault()
                              handleRemoveContactClick(contact.id, getContactDisplayName(contact))
                            }}
                          >
                            <X className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                          </Button>
                        </ActionGate>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <Empty className="border border-dashed py-8">
                  <EmptyMedia variant="icon">
                    <Users className="h-5 w-5" />
                  </EmptyMedia>
                  <EmptyDescription>No contacts associated with this company</EmptyDescription>
                </Empty>
              )}
            </CardContent>
          </Card>

          {/* Activity (Notes & Tasks) */}
          {company && (
            <ActivityTabs
              organizationId={company.organizationId}
              parentType={ParentTypeEnum.PARENT_TYPE_COMPANY}
              parentId={company.id}
              notes={notes}
              tasks={tasks}
              attachments={attachments}
              customerEmail={contacts.find((c) => c.contact?.email)?.contact?.email}
            />
          )}
        </div>
      </div>

      {/* Edit Sheet */}
      <CompanyCreateEditSheet
        organizationId={company.organizationId}
        company={company}
        open={editSheetOpen}
        onOpenChange={setEditSheetOpen}
      />

      {/* Add Contact Dialog */}
      <AddContactToCompanyDialog
        companyId={id}
        companyName={company.name}
        open={addContactDialogOpen}
        onOpenChange={setAddContactDialogOpen}
        onSuccess={() => refetch()}
      />

      {/* Remove Contact Confirmation Dialog */}
      <RemoveContactFromCompanyDialog
        companyId={id}
        companyName={company.name}
        contactId={contactToRemove?.id ?? null}
        contactName={contactToRemove?.name ?? ''}
        open={removeContactDialogOpen}
        onOpenChange={setRemoveContactDialogOpen}
      />
    </div>
  )
}
